/**
 * DashboardOverview Component
 * Analytics home with stats, charts, and completion metrics
 * Uses pure CSS/SVG for charts — no external charting library
 */
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Clock, Activity, CheckCircle2, BarChart3, Plus } from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/design-system.css';

function DashboardOverview({ adminUser, onNavigate }) {
    const [stats, setStats] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [recentSurveys, setRecentSurveys] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('adminToken');
        const swaPrincipal = localStorage.getItem('swaPrincipal');
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
        };
    };

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            const headers = getAuthHeaders();

            const [statsRes, deptsRes, sessionsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin/stats`, { headers }).then(r => {
                    if (!r.ok) throw new Error('Stats fetch failed');
                    return r.json();
                }),
                fetch(`${API_BASE_URL}/api/admin/departments`, { headers }).then(r => {
                    if (!r.ok) throw new Error('Departments fetch failed');
                    return r.json();
                }),
                fetch(`${API_BASE_URL}/api/admin/sessions?page=1&page_size=5&sort_by=created_at&sort_order=desc`, { headers }).then(r => {
                    if (!r.ok) throw new Error('Sessions fetch failed');
                    return r.json();
                }),
            ]);

            setStats(statsRes);
            setDepartments(deptsRes);
            setRecentSurveys(sessionsRes.items || sessionsRes);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    /* ── Chart helpers ── */

    // SVG donut arc helper
    const describeArc = (cx, cy, r, startAngle, endAngle) => {
        const rad = (a) => ((a - 90) * Math.PI) / 180;
        const start = { x: cx + r * Math.cos(rad(endAngle)), y: cy + r * Math.sin(rad(endAngle)) };
        const end = { x: cx + r * Math.cos(rad(startAngle)), y: cy + r * Math.sin(rad(startAngle)) };
        const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
        return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return '#34C759';
            case 'in_progress': return '#FF9500';
            default: return '#86868B';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'completed': return 'Completed';
            case 'in_progress': return 'In Progress';
            default: return 'Not Started';
        }
    };

    /* ── Loading state ── */
    if (isLoading) {
        return (
            <div className="ov-loading">
                <div className="dash-loading-spinner" />
                <p>Loading analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-error)' }}>
                <p>Failed to load overview: {error}</p>
                <button className="ds-btn ds-btn-primary" onClick={loadData}>Retry</button>
            </div>
        );
    }

    /* ── Donut chart data ── */
    const total = stats?.total_sessions || 1;
    const completed = stats?.completed_sessions || 0;
    const inProgress = stats?.in_progress_sessions || 0;
    const notStarted = stats?.not_started_sessions || 0;

    const completedPct = (completed / total) * 100;
    const inProgressPct = (inProgress / total) * 100;
    const notStartedPct = (notStarted / total) * 100;

    // Build arcs for donut
    const donutSegments = [];
    let currentAngle = 0;
    if (completedPct > 0) {
        const sweep = (completedPct / 100) * 360;
        donutSegments.push({ color: '#34C759', start: currentAngle, end: currentAngle + sweep, label: 'Completed', pct: completedPct, count: completed });
        currentAngle += sweep;
    }
    if (inProgressPct > 0) {
        const sweep = (inProgressPct / 100) * 360;
        donutSegments.push({ color: '#FF9500', start: currentAngle, end: currentAngle + sweep, label: 'In Progress', pct: inProgressPct, count: inProgress });
        currentAngle += sweep;
    }
    if (notStartedPct > 0) {
        const sweep = (notStartedPct / 100) * 360;
        donutSegments.push({ color: '#86868B', start: currentAngle, end: currentAngle + sweep, label: 'Not Started', pct: notStartedPct, count: notStarted });
        currentAngle += sweep;
    }

    /* ── Department chart data ── */
    // We need department-level counts. Use departments list + recent data.
    // For now we'll build from stats if available, otherwise from session data
    const deptCounts = {};
    recentSurveys.forEach(s => {
        deptCounts[s.department] = (deptCounts[s.department] || 0) + 1;
    });

    return (
        <div>
            {/* ── Page header ── */}
            <div className="ds-page-header">
                <div className="ds-page-header-top">
                    <div>
                        <h1 className="ds-page-title">Dashboard</h1>
                        <p className="ds-page-subtitle">Welcome back, {adminUser?.username || 'Admin'}. Here's your work study overview.</p>
                    </div>
                    <div className="ds-page-actions">
                        <button className="ds-btn ds-btn-primary" onClick={() => onNavigate('create')}>
                            <Plus size={18} />
                            New Work Study
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Stat Cards Row ── */}
            <div className="ds-stats-grid">
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Total Studies</span>
                        <div className="ds-stat-icon blue">
                            <FileText size={18} />
                        </div>
                    </div>
                    <div className="ds-stat-value">{stats?.total_sessions || 0}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Not Started</span>
                        <div className="ds-stat-icon orange">
                            <Clock size={18} />
                        </div>
                    </div>
                    <div className="ds-stat-value">{notStarted}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">In Progress</span>
                        <div className="ds-stat-icon orange">
                            <Activity size={18} />
                        </div>
                    </div>
                    <div className="ds-stat-value">{inProgress}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Completed</span>
                        <div className="ds-stat-icon green">
                            <CheckCircle2 size={18} />
                        </div>
                    </div>
                    <div className="ds-stat-value">{completed}</div>
                </div>
                <div className="ds-stat-card">
                    <div className="ds-stat-header">
                        <span className="ds-stat-label">Completion Rate</span>
                        <div className="ds-stat-icon purple">
                            <BarChart3 size={18} />
                        </div>
                    </div>
                    <div className="ds-stat-value">{stats?.completion_rate || 0}%</div>
                </div>
            </div>

            {/* ── Charts Row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }}>
                {/* Donut Chart — Status Distribution */}
                <div className="ds-card">
                    <div className="ds-card-header">
                        <h3 className="ds-card-title">Status Distribution</h3>
                    </div>
                    <div className="ds-card-body" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)' }}>
                        <svg viewBox="0 0 200 200" className="ov-donut-svg" style={{ width: 160, height: 160, flexShrink: 0 }}>
                            {total === 0 ? (
                                <circle cx="100" cy="100" r="70" fill="none" stroke="#D2D2D7" strokeWidth="24" />
                            ) : donutSegments.length === 1 ? (
                                <circle cx="100" cy="100" r="70" fill="none" stroke={donutSegments[0].color} strokeWidth="24" />
                            ) : (
                                donutSegments.map((seg, i) => (
                                    <path
                                        key={i}
                                        d={describeArc(100, 100, 70, seg.start, seg.end - 0.5)}
                                        fill="none"
                                        stroke={seg.color}
                                        strokeWidth="24"
                                        strokeLinecap="butt"
                                    />
                                ))
                            )}
                            {/* Center text */}
                            <text x="100" y="92" textAnchor="middle" style={{ fontSize: 28, fontWeight: 700, fill: 'var(--color-text-primary)' }}>{stats?.completion_rate || 0}%</text>
                            <text x="100" y="114" textAnchor="middle" style={{ fontSize: 12, fill: 'var(--color-text-tertiary)' }}>Complete</text>
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {donutSegments.map((seg, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: seg.color, flexShrink: 0 }} />
                                    <span style={{ color: 'var(--color-text-secondary)', minWidth: 80 }}>{seg.label}</span>
                                    <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', minWidth: 24 }}>{seg.count}</span>
                                    <span style={{ color: 'var(--color-text-tertiary)' }}>{Math.round(seg.pct)}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bar Chart — Department Breakdown */}
                <div className="ds-card">
                    <div className="ds-card-header">
                        <h3 className="ds-card-title">Departments</h3>
                    </div>
                    <div className="ds-card-body">
                        {departments.length === 0 ? (
                            <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>No department data yet.</p>
                        ) : (
                            departments.map((dept, i) => {
                                const count = deptCounts[dept] || 0;
                                const maxCount = Math.max(...Object.values(deptCounts), 1);
                                const barPct = Math.max((count / maxCount) * 100, 4);
                                return (
                                    <div key={dept} className="ds-bar-row">
                                        <span className="ds-bar-label">{dept}</span>
                                        <div className="ds-bar-track">
                                            <div className="ds-bar-fill" style={{ width: `${barPct}%`, animationDelay: `${i * 80}ms` }} />
                                        </div>
                                        <span className="ds-bar-value">{count}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* ── Recent Activity ── */}
            <div className="ds-card">
                <div className="ds-card-header">
                    <h3 className="ds-card-title">Recent Studies</h3>
                    <button className="ds-btn ds-btn-ghost" onClick={() => onNavigate('surveys')}>View all →</button>
                </div>
                {recentSurveys.length === 0 ? (
                    <div className="ds-card-body">
                        <p style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>No surveys created yet.</p>
                    </div>
                ) : (
                    <div className="ov-recent-list">
                        {recentSurveys.map(s => (
                            <div key={s.id} className="ov-recent-item">
                                <div className="ds-avatar">
                                    {(s.employee_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="ov-recent-info">
                                    <span className="ov-recent-name">{s.employee_name}</span>
                                    <span className="ov-recent-role">{s.role_title} · {s.department}</span>
                                </div>
                                <span className={`ov-recent-status ov-status--${s.status}`}>
                                    {getStatusLabel(s.status)}
                                </span>
                                <span className="ov-recent-date">{formatDate(s.created_at)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default DashboardOverview;
